import { UiPoolDataProvider } from '@aave/contract-helpers'
import { formatReserves } from '@aave/math-utils'
import * as markets from '@bgd-labs/aave-address-book'
import { base, mainnet } from 'viem/chains'

import {
  AUTO_EARN_MAINNET_V2_MODULE_ADDRESS,
  AUTO_EARN_V1_MODULE_ADDRESS,
  AUTO_EARN_V2_MODULE_ADDRESS,
  WORKSPACE_AUTO_EARN_ENROLLER_ADDRESSES,
  WorkspaceAutoEarnChainId,
} from '~/constants/addresses'
import { IAddress, OrgSmartAccount, SmartAccountRole } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { getEthersProvider } from '~/utils/requests'

// hardcoding these address for now, will mostly either move it to a better config that stays in code or turns into a table.
export const AAVE_BASE_USDC: Address.Type = EvmAddress.format(
  '0x4EA71A20e655794051D1eE8b6e4A3269B13ccaCc',
)
export const MORPHO_FEE_WRAPPER_BASE_USDC: Address.Type = EvmAddress.format(
  '0x189A1a23F46321a196646314E6a078a404513C8F',
)
export const MORPHO_FEE_WRAPPER_MAINNET_USDC: Address.Type = EvmAddress.format(
  '0xF5730f6F5bcc2a3E98dA199F40b1A8aF1Ac37A43',
)
export const getAutoEarnModuleAddress = () => AUTO_EARN_V2_MODULE_ADDRESS

export const getAutoEarnVault = () => MORPHO_FEE_WRAPPER_BASE_USDC

export const getVaultProviderName = (vault: IAddress) =>
  [MORPHO_FEE_WRAPPER_BASE_USDC, MORPHO_FEE_WRAPPER_MAINNET_USDC].includes(
    Address.format(vault),
  )
    ? 'Morpho'
    : 'Aave'

const matchesModule = ({
  module,
  moduleAddress,
  chainId,
}: {
  module: OrgSmartAccount['smartAccountModules'][number]
  moduleAddress: Address.Type
  chainId: number
}) =>
  module.chainId === chainId &&
  Address.format(module.moduleAddress) === moduleAddress

// Pick so admin shapes with the same module rows can reuse these checks.
// smartAccountModules can be missing at runtime on partially-loaded accounts
// (e.g. an org-list object seeding the detail query), so treat that as off.
const isModuleEnabled = ({
  smartAccount,
  moduleAddress,
  chainId,
}: {
  smartAccount: Pick<OrgSmartAccount, 'smartAccountModules'>
  moduleAddress: Address.Type
  chainId: number
}) =>
  smartAccount.smartAccountModules?.some(
    (module) =>
      matchesModule({ module, moduleAddress, chainId }) && module.isEnabled,
  ) ?? false

export const isAutoEarnV1Enabled = (
  smartAccount: Pick<OrgSmartAccount, 'smartAccountModules'>,
) =>
  isModuleEnabled({
    smartAccount,
    moduleAddress: AUTO_EARN_V1_MODULE_ADDRESS,
    chainId: base.id,
  })

export const isAutoEarnV2Enabled = (
  smartAccount: Pick<OrgSmartAccount, 'smartAccountModules'>,
) =>
  isModuleEnabled({
    smartAccount,
    moduleAddress: AUTO_EARN_V2_MODULE_ADDRESS,
    chainId: base.id,
  })

// The enroller lives on the Treasury. While it's enabled on a network, the
// Treasury and its sub-accounts there are enrolled in auto-earn.
export const isWorkspaceAutoEarnEnabled = ({
  treasury,
  chainId,
}: {
  treasury: Pick<OrgSmartAccount, 'smartAccountModules'>
  chainId: WorkspaceAutoEarnChainId
}) =>
  isModuleEnabled({
    smartAccount: treasury,
    moduleAddress: WORKSPACE_AUTO_EARN_ENROLLER_ADDRESSES[chainId],
    chainId,
  })

// The module each chain's auto-earn sweep deposits through.
const AUTO_EARN_SWEEP_MODULES: Partial<Record<number, Address.Type>> = {
  [base.id]: AUTO_EARN_V2_MODULE_ADDRESS,
  [mainnet.id]: AUTO_EARN_MAINNET_V2_MODULE_ADDRESS,
}

// The auto-earn module row for a chain, which carries the withdrawal pause.
export const getAutoEarnModule = (
  smartAccount: Pick<OrgSmartAccount, 'smartAccountModules'>,
  chainId: number,
) =>
  smartAccount.smartAccountModules?.find(
    (module) =>
      module.chainId === chainId &&
      Address.format(module.moduleAddress) ===
        AUTO_EARN_SWEEP_MODULES[chainId] &&
      module.isEnabled,
  )

export const isAutoEarnEnabled = (smartAccount: OrgSmartAccount) =>
  isAutoEarnV2Enabled(smartAccount)

// An account still running module 1.0 must go through the org
// migration before the 2.0 toggle — the migration is the only flow that
// disables 1.0, so enabling 2.0 directly would leave 1.0 dangling and keep
// the migration banner showing.
export const needsEarnMigrationFirst = (smartAccount: OrgSmartAccount) =>
  isAutoEarnV1Enabled(smartAccount) && !isAutoEarnV2Enabled(smartAccount)

export const getAutoEarnVersionLabel = (
  smartAccount: Pick<OrgSmartAccount, 'smartAccountModules'>,
) => {
  if (isAutoEarnV2Enabled(smartAccount)) return 'Yes (2.0)'
  if (isAutoEarnV1Enabled(smartAccount)) return 'Yes (1.0)'
  return 'No'
}

// Mirrors a confirmed enable/disable into the cached account before the
// server catches up.
export const upsertModuleEnabled = ({
  modules,
  moduleAddress,
  enabled,
}: {
  modules: OrgSmartAccount['smartAccountModules']
  moduleAddress: Address.Type
  enabled: boolean
}): OrgSmartAccount['smartAccountModules'] =>
  modules.some((module) =>
    matchesModule({ module, moduleAddress, chainId: base.id }),
  )
    ? modules.map((module) =>
        matchesModule({ module, moduleAddress, chainId: base.id })
          ? { ...module, isEnabled: enabled }
          : module,
      )
    : [...modules, { chainId: base.id, moduleAddress, isEnabled: enabled }]

export const BASE_USDC: Address.Type = EvmAddress.format(
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
)
export const MAINNET_USDC: Address.Type = EvmAddress.format(
  '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
)

// keep in sync with server/src/utils/tokens/vault.ts
// Mapping of vaults enabled for each asset per chain.
const ASSET_VAULT_MAPPING: Record<number, Record<IAddress, IAddress[]>> = {
  [base.id]: {
    [BASE_USDC]: [AAVE_BASE_USDC, MORPHO_FEE_WRAPPER_BASE_USDC],
  },
  [mainnet.id]: {
    [MAINNET_USDC]: [MORPHO_FEE_WRAPPER_MAINNET_USDC],
  },
}

/** Vaults retained for historical display and withdrawals, but not new deposits. */
const LEGACY_VAULTS = new Set<IAddress>([AAVE_BASE_USDC])

export const isLegacyVault = (vault: IAddress) =>
  LEGACY_VAULTS.has(Address.format(vault))

const ASSET_DATA: Record<
  number,
  Record<IAddress, { symbol: string; decimals: number }>
> = {
  [base.id]: {
    [BASE_USDC]: {
      symbol: 'USDC',
      decimals: 6,
    },
  },
  [mainnet.id]: {
    [MAINNET_USDC]: { symbol: 'USDC', decimals: 6 },
  },
}

const VAULT_ADDRESSES_PER_CHAIN: Record<number, string[]> = Object.fromEntries(
  Object.entries(ASSET_VAULT_MAPPING).map(([chainId, tokens]) => [
    Number(chainId),
    Object.values(tokens).flat(),
  ]),
)

const VAULT_ASSET_MAPPING: Record<
  number,
  Record<IAddress, IAddress>
> = Object.fromEntries(
  Object.entries(ASSET_VAULT_MAPPING).map(([chainId, tokens]) => [
    Number(chainId),
    Object.fromEntries(
      Object.entries(tokens).flatMap(([asset, vaults]) =>
        vaults.map((vault) => [vault, asset as IAddress]),
      ),
    ),
  ]),
)

export const getVaultAsset = (
  chainId: number,
  vault: IAddress,
): IAddress | undefined => {
  // Other tokens (and placeholder addresses) are not vaults. In particular,
  // mainnet now has a registry entry, so validate before formatting a token.
  if (!VAULT_ASSET_MAPPING[chainId] || !Address.validate(vault))
    return undefined
  return VAULT_ASSET_MAPPING[chainId][Address.format(vault)]
}

export const getVaultAssetSymbol = (chainId: number, vault: IAddress) => {
  return getVaultAssetData(chainId, vault)?.symbol
}

// Underlying asset + registry metadata; undefined for non-vault tokens.
export const getVaultAssetData = (chainId: number, vault: IAddress) => {
  const asset = getVaultAsset(chainId, vault)
  if (!asset) return undefined
  const data = ASSET_DATA[chainId]?.[asset]
  return data && { asset, ...data }
}

export const isVaultToken = (chainId: number, address: IAddress) => {
  return VAULT_ADDRESSES_PER_CHAIN[chainId]?.includes(Address.format(address))
}

export const getVaultsForAsset = (chainId: number, asset: IAddress) => {
  return ASSET_VAULT_MAPPING[chainId]?.[Address.format(asset)] ?? []
}

/** Networks that take new Earn deposits, Base first. Mainnet stays hidden until the workspace rollout flag is enabled. */
export const getEarnDepositChainIds = (
  mainnetEarnEnabled: boolean,
): (typeof base.id | typeof mainnet.id)[] =>
  mainnetEarnEnabled ? [base.id, mainnet.id] : [base.id]

export const getEarnDepositVaults = (
  chainId: number,
  asset: IAddress,
  mainnetEarnEnabled: boolean,
) => {
  if (!getEarnDepositChainIds(mainnetEarnEnabled).some((id) => id === chainId))
    return []
  return getVaultsForAsset(chainId, asset).filter((vault) =>
    [MORPHO_FEE_WRAPPER_BASE_USDC, MORPHO_FEE_WRAPPER_MAINNET_USDC].includes(
      Address.format(vault),
    ),
  )
}

export const getApyPhrase = (apy?: number) => (apy ? `${apy}% APY` : 'yield')

export const getEarnLabel = (apy?: number) => `Earn ${getApyPhrase(apy)}`

export const supportsAutoEarn = (smartAccount: Pick<OrgSmartAccount, 'role'>) =>
  smartAccount.role === SmartAccountRole.TREASURY ||
  smartAccount.role === SmartAccountRole.OPERATING

export const isAutoEarnTransfer = (transfer: {
  chainId: number
  transferType: string
  tokenAddress: string
  to: string
}): boolean => {
  return (
    transfer.transferType === 'Outbound' &&
    isVaultAssetPair({
      chainId: transfer.chainId,
      vault: transfer.to as IAddress,
      asset: transfer.tokenAddress as IAddress,
    })
  )
}

export const isVaultAssetPair = ({
  chainId,
  vault,
  asset,
}: {
  chainId: number
  vault: IAddress
  asset: IAddress
}) => {
  return (
    VAULT_ASSET_MAPPING[chainId]?.[Address.format(vault)] ===
    Address.format(asset)
  )
}

const getPoolDataProvider = (chainId: number) => {
  if (chainId === base.id)
    return new UiPoolDataProvider({
      uiPoolDataProviderAddress: markets.AaveV3Base.UI_POOL_DATA_PROVIDER,
      provider: getEthersProvider(chainId),
      chainId,
    })
  throw new Error(`No provider available for chain:${chainId}`)
}

const getPoolAddressProvider = (chainId: number) => {
  if (chainId === base.id) return markets.AaveV3Base.POOL_ADDRESSES_PROVIDER
  throw new Error(`No address provider available for chain:${chainId}`)
}

const getMorphoVaultApy = async (chainId: number, address: IAddress) => {
  // APY is a time-windowed, net return across the vault's allocations; a
  // current on-chain share-price snapshot cannot calculate it on its own.
  const response = await fetch('https://api.morpho.org/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `query VaultApy($address: String!, $chainId: Int!) { vaultV2ByAddress(address: $address, chainId: $chainId) { netApy } }`,
      variables: { address, chainId },
    }),
  })
  if (!response.ok) throw new Error('Failed to fetch Morpho vault APY')
  const { data, errors } = (await response.json()) as {
    data?: { vaultV2ByAddress?: { netApy?: number } }
    errors?: { message?: string }[]
  }
  if (errors?.length)
    throw new Error(errors.map((error) => error.message).join(', '))
  // netApy on the wrapper is already net of its performance fee.
  const apy = data?.vaultV2ByAddress?.netApy
  if (apy === undefined || apy === null)
    throw new Error('Failed to fetch Morpho vault APY')

  return parseFloat((apy * 100).toFixed(2))
}

export const getVaultApy = async (chainId: number, address: IAddress) => {
  if (
    address === MORPHO_FEE_WRAPPER_BASE_USDC ||
    address === MORPHO_FEE_WRAPPER_MAINNET_USDC
  )
    return getMorphoVaultApy(chainId, address)

  const poolDataProvider = getPoolDataProvider(chainId)
  const reserves = await poolDataProvider.getReservesHumanized({
    lendingPoolAddressProvider: getPoolAddressProvider(chainId),
  })

  const formattedReserves = formatReserves({
    reserves: reserves.reservesData,
    currentTimestamp: new Date().getUTCSeconds(),
    marketReferenceCurrencyDecimals:
      reserves.baseCurrencyData.marketReferenceCurrencyDecimals,
    marketReferencePriceInUsd:
      reserves.baseCurrencyData.marketReferenceCurrencyPriceInUsd,
  })

  const poolReserves = formattedReserves.find(
    (r) =>
      getVaultAsset(chainId, address) === Address.format(r.underlyingAsset),
  )

  if (!poolReserves) throw new Error(`Failed to fetch pool reserves`)

  return parseFloat((parseFloat(poolReserves.supplyAPY) * 100).toFixed(2))
}
